import {
  Footprints,
  MapPin,
  MoreHorizontal,
  Plus,
  Receipt,
  Search,
  Sun,
  TramFront,
  Utensils,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Button, DataTag, DateFlag, ItineraryRow, Leg, MoneyReadout, TagGroup } from '../kit/index.ts';
import { withNumerals } from '../kit/SectionHeader.tsx';
import t from '../kit/type.module.css';
import s from './Today.module.css';

// S-04 Today: the day plate (travel-documents.md §4) in its fixed order: header strip, plan view,
// profile, bookings, items. Static sample data, the same day as the SwiftUI TodayView (T-DSN-01).
const DAY = {
  kicker: '// Kyoto · JP26-A',
  flag: '11.08',
  weekday: 'Sun',
  counter: 'Day 06 / 10',
  spoken: 'Sunday 8 November, day 6 of 10, Kyoto, today',
  weather: '14°C · Sunrise 06:21 · Sunset 16:57',
  ruler: { start: 6 * 60, end: 22 * 60, now: 10 * 60 + 12 },
  blocks: [
    { start: 7 * 60 + 30, end: 9 * 60 + 10, done: true },
    { start: 10 * 60 + 30, end: 12 * 60, done: false },
    { start: 12 * 60 + 30, end: 13 * 60 + 30, done: false },
    { start: 14 * 60 + 30, end: 15 * 60 + 56, done: false },
    { start: 16 * 60, end: 17 * 60 + 30, done: false },
    { start: 19 * 60 + 30, end: 21 * 60, done: false },
  ],
};

export function Today() {
  return (
    <div className={s.screen}>
      <nav className={s.bar} aria-label="Day actions">
        <button type="button" className={s.round} aria-label="Search">
          <Search aria-hidden="true" />
        </button>
        <button type="button" className={s.round} aria-label="More">
          <MoreHorizontal aria-hidden="true" />
        </button>
      </nav>
      <main className={s.plate}>
        <header className={s.header}>
          <div className={`${t.displayS} ${s.kicker}`}>{DAY.kicker}</div>
          <h1 className={s.strip} aria-label={DAY.spoken}>
            <span className={s.flagGroup} aria-hidden="true">
              <DateFlag text={DAY.flag} today />
              <span className={`${t.label} ${s.muted}`}>{DAY.weekday}</span>
            </span>
            <span className={t.displayL} aria-hidden="true">
              {withNumerals(DAY.counter)}
            </span>
          </h1>
          <p className={`${t.micro} ${s.weather}`}>
            <Sun aria-hidden="true" />
            {DAY.weather}
          </p>
        </header>

        {/* The map arrives with the map package (MS-1D); the plate keeps its size and real readouts. */}
        <section className={s.map} aria-label="Map of the day">
          <span className={s.mapTopLeft}>
            <DataTag label="↓ 06-1" value="3.2 km" spoken="Next stop 06-1" />
          </span>
          <span className={s.mapBottomRight}>
            <DataTag label="Today" value="2.9 / 7.4 km" spoken="Walked today" />
          </span>
        </section>

        <DayRuler />

        <section className={s.booking} aria-label="Booking today">
          <span className={s.bookingChip} aria-hidden="true">
            <Utensils strokeWidth={2.5} />
          </span>
          <div className={s.bookingText}>
            <h2 className={`${t.bodyS} ${s.bookingTitle}`}>Dinner · Pontocho Kappa</h2>
            <TagGroup>
              <DataTag label="19:30" value="2 pax" />
              <DataTag label="Conf" value="4821" spoken="Confirmation code" />
            </TagGroup>
          </div>
          <span className={`${t.readoutS} ${s.stop}`}>06-6</span>
        </section>

        <section className={s.items} aria-label="Plan">
          <ItineraryRow
            time="07:30"
            icon={MapPin}
            title="Fushimi Inari Taisha"
            subline="Visited 07:41–09:20 · 2.9 km walked"
            stopId="06-1"
            state="past"
          />
          <ItineraryRow
            time="09:35"
            zone="Logged"
            icon={Receipt}
            title="Coffee · Vermillion"
            variant="logged"
            money={<MoneyReadout primary="¥580" secondary={['₱223', '€3.52']} variant="inline" />}
            accessory={<DataTag label="Mine" tone="mine" spoken="Personal, only you can see this" />}
          />
          <Leg icon={TramFront} text="Keihan · 18 min" />
          <ItineraryRow
            time="10:30"
            icon={MapPin}
            title="Kiyomizu-dera"
            subline="// Higashiyama · 1 h 30 · Partner is here"
            stopId="06-2"
            state="now"
            tags={[
              <DataTag key="open" label="Open" value="06:00–18:00" spoken="Open" />,
              <DataTag key="fee" label="Fee" value="¥500" spoken="Entry fee" />,
            ]}
          />
          <ItineraryRow icon={Footprints} title="Sannenzaka & Ninenzaka" stopId="06-2a" variant="sub" />
          <Leg icon={Footprints} text="7 min · 0.5 km" />
          <ItineraryRow
            time="12:30"
            icon={Utensils}
            title="Okutan Kiyomizu"
            subline="Yudofu lunch · reserved"
            stopId="06-3"
          />
          <Leg icon={Footprints} text="14 min · 1.0 km" />
          <ItineraryRow
            time="14:30"
            icon={MapPin}
            title="Yasaka Shrine → Gion"
            subline="// Gion · 1 h"
            stopId="06-4"
          />
        </section>
      </main>
      {/* Quick log is ink, not signal: the date flag is this view's signal block (P2). */}
      <div className={s.log}>
        <Button variant="ink" icon={Plus} leader>
          Log
        </Button>
      </div>
    </div>
  );
}

/** The profile: a time ruler with one block per item, done blocks filled, and the now marker. */
function DayRuler() {
  const { start, end, now } = DAY.ruler;
  const at = (minute: number) => `${((minute - start) / (end - start)) * 100}%`;
  const hhmm = `${String(Math.floor(now / 60)).padStart(2, '0')}:${String(now % 60).padStart(2, '0')}`;
  const done = DAY.blocks.filter((b) => b.done).length;
  const marks: ReactNode[] = [6, 9, 12, 15, 18, 21].map((h) => (
    <span key={h} className={s.tick} style={{ left: at(h * 60) }}>
      {String(h).padStart(2, '0')}
    </span>
  ));
  return (
    <div
      className={s.ruler}
      role="img"
      aria-label={`Day timeline, 06:00 to 22:00: ${DAY.blocks.length} items, ${done} done, now ${hhmm}`}
    >
      <span className={s.base} />
      {DAY.blocks.map((b) => (
        <span
          key={b.start}
          className={b.done ? `${s.block} ${s.done}` : s.block}
          style={{ left: at(b.start), width: at(start + b.end - b.start) }}
        />
      ))}
      <span className={s.now} style={{ left: at(now) }} />
      <span className={`${t.micro} ${s.ticks}`}>{marks}</span>
    </div>
  );
}
